import { HttpBackend, HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CalAngularService } from '@cvx/cal-angular';
import { catchError, concatMap, map, Observable, of, throwError } from 'rxjs';
import type { Config as CalConfig } from '@cvx/cal-angular';
import config from 'src/assets/config.json';

@Injectable({
  providedIn: 'root'
})
export class GraphService {

  private readonly calService = inject(CalAngularService);
  private readonly httpBackend = inject(HttpBackend);

  // Create a new HttpClient instance using the HttpBackend to bypass interceptors
  private readonly httpBackendClient = new HttpClient(this.httpBackend);


  /**
   * Retrieves the user's profile picture from Microsoft Graph.
   * Chains the AAD token acquisition and the photo request.
   * @returns Observable emitting the user's photo as a Blob, or null if retrieval fails.
   */
  public getProfilePicture(): Observable<Blob | null> {
    const graphPhotoEndpoint = "https://graph.microsoft.com/v1.0/me/photo/$value";

    return this.getAADToken((config as CalConfig).oidcScopes).pipe(
      concatMap(token => {
        return this.httpBackendClient.get(graphPhotoEndpoint, {
          responseType: 'blob',
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
      }),
      catchError(error => {
        console.error('Failed to retrieve user photo from Graph API:', error);
        return of(null);
      })
    );    
  }


  /**
   * Requests an Azure Active Directory token for the specified scopes.
   * Throws an error if the token cannot be acquired.
   * @param scopes Array of OAuth scopes required for the token.
   * @returns Observable emitting the token as a string.
   */
  private getAADToken(scopes: string[]): Observable<string> {

    return this.calService.getAADToken(scopes).pipe(
      map(token => {
        if (!token) {
          console.error('Failed to acquire AAD token for scope.');
          throw new Error('Failed to acquire AAD token for scope');
        }

        return <string>token;
      }),  
      catchError(error => {
        console.error('Failed to acquire AAD token for scope:', error);
        return throwError(() => error);
      })
    );
  } 
  
}
